import { PORTS, landingPoint } from './voyage.js';
import { mobileLayout } from './mobileLayout.js';

// The compass uses camera-relative directions, so rotating the view never
// sends the player the wrong way. A pin is a destination, not a fast travel.
export function compassTarget(voyage, position, azimuth) {
  const island = PORTS.find(i => i.id === voyage.trackedId);
  if (!island) return null;
  const point = landingPoint(island);
  const dx = point.x - position.x, dz = point.z - position.z;
  const forwardX = -Math.cos(azimuth), forwardZ = -Math.sin(azimuth);
  const rightX = -forwardZ, rightZ = forwardX;
  const angle = Math.atan2(dx * rightX + dz * rightZ, dx * forwardX + dz * forwardZ);
  const arrows = ['↑', '↗', '→', '↘', '↓', '↙', '←', '↖'];
  const sector = (Math.round(angle / (Math.PI / 4)) + 8) % 8;
  const distance = Math.hypot(dx, dz);
  return { id: island.id, name: voyage.discovered.includes(island.id) ? island.name : '未知岛屿',
    arrow: arrows[sector], distance: Math.round(distance), nearby: distance < 4.5 };
}

export function islandNotes(state, island) {
  const v = state.voyage;
  const recorded = state.beaconQuest?.seals?.includes(island.id);
  const clues = {
    spring: recorded ? '泉水印记已记录 · 铭石在泉池旁泛着微光' : '泉池旁有水纹铭石 · 调查它寻找航标印记',
    grove: recorded ? '椰林印记已记录 · 风铃贝随海风轻响' : '沿海风寻找风铃贝 · 调查椰林印记',
    ruins: recorded ? '三石共鸣 · 遗迹印记已记录' : '观察古老碑文 · 依次触碰三块符文石',
    beacon: state.beaconQuest?.lit ? '航标已点亮 · 夜间的暖光照向归途' : '寻找三枚航标印记 · 在灯塔前亲手点亮航标',
  };
  if (island.id === 'home') return { name: island.name, detail: '交易、休息、维修和农田都在家园', status: '家园 · 可随时规划返航' };
  if (!v.discovered.includes(island.id)) return { name: '未知岛屿', detail: '远岛藏着航标印记 · 驶近后辨认地标', status: '尚未发现 · 可以标记后自由探索' };
  if (!v.visited.includes(island.id)) return { name: island.name, detail: clues[island.id] || island.description, status: '已发现 · 靠近浮标，减速后下船踏勘' };
  const previous = v.harvested[`${island.id}-main`];
  const resource = previous == null ? '尚未采集'
    : island.resource === 'treasure' ? '宝箱已开启'
      : state.gameMinutes >= previous + 1440 ? '资源已补充'
        : `资源约 ${Math.ceil((previous + 1440 - state.gameMinutes) / 60)} 游戏小时后补充`;
  return { name: island.name, detail: clues[island.id] || island.description, status: `已踏勘 · ${resource}` };
}

export function explorationLayout(screen, { statusRect } = {}) {
  const layout = mobileLayout(screen), safe = layout.safe;
  const status = statusRect || layout.status;
  const width = Math.min(layout.compact ? (layout.landscape ? 270 : screen.width * .29) : Math.min(330, screen.width * .20), screen.width - safe.left - safe.right - 16);
  const shortLandscape = layout.compact && layout.landscape;
  const compass = { x: shortLandscape ? Math.max(safe.left+8,screen.width - safe.right - width - 72) : safe.left + (screen.width - safe.left - safe.right - width) / 2 + (layout.compact && !layout.landscape ? screen.width * .035 : 0),
    y: safe.top + 18, width, height: 48 };
  const profile = { x: screen.width - safe.right - 58, y: safe.top + 14, width: 44, height: 44 };
  const intersects = rect => compass.x < rect.x + rect.width && compass.x + compass.width > rect.x
    && compass.y < rect.y + rect.height && compass.y + compass.height > rect.y;
  if (layout.compact && !layout.landscape) {
    const rightOfStatus = status.x + status.width + 8;
    if (rightOfStatus + width <= profile.x - 8) compass.x = rightOfStatus;
    else {
      compass.x = safe.left + (screen.width - safe.left - safe.right - width) / 2;
      compass.y = Math.max(compass.y, status.y + status.height + 8, profile.y + profile.height + 8);
    }
  }
  if (intersects(status) || intersects(profile)) compass.y = Math.max(compass.y, status.y + status.height + 8, profile.y + profile.height + 8);
  return { compass };
}

export function discoveryNoticeLayout(screen, { statusRect, navRect, controlsRect } = {}) {
  const safe = mobileLayout(screen).safe;
  const compass = explorationLayout(screen, { statusRect }).compass;
  const width = Math.min(400, screen.width - safe.left - safe.right - 24);
  const height = 78;
  const y = Math.max(safe.top + 78, compass.y + compass.height + 12, (statusRect?.y || 0) + (statusRect?.height || 0) + 12);
  if (y + height + 12 > Math.min(navRect?.y ?? screen.height - safe.bottom, controlsRect?.y ?? screen.height - safe.bottom)) return null;
  return { x: safe.left + (screen.width - safe.left - safe.right - width) / 2, y, width, height };
}
